/* eslint-disable max-lines -- most lines are defining providers */
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
} from '@angular/core';
import { isEqualAtDepth } from '@sama/js-core';
import { get } from '@sama/micro-dash';
import { distinctUntilChanged, map, merge, skip } from 'rxjs';
import { AbstractContentInitializer } from '../../cross-team-shared/abstract-content-initializer';
import { makeToolProviders } from '../../cross-team-shared/annotation/shared/abstract-tool';
import { contentProviders } from '../../cross-team-shared/content-providers';
import { MouseEventService } from '../../cross-team-shared/misc-services/events/mouse-event.service';
import { observeShapeGroupOrderChanges } from '../../cross-team-shared/misc-utils';
import { keys } from '../../cross-team-shared/misc-utils/keys';
import { AbstractSensorContentStore } from '../../cross-team-shared/state/abstract-sensor-content-store';
import { ShapeGroupOrderState } from '../../team-apis/content/content-connector';
import { CalibrationService } from '../misc-services/calibration.service';
import { ProjectionService } from '../misc-services/projection.service';
import { SideViewRotationService } from '../misc-services/side-view-rotation.service';
import { UnprojectionService } from '../misc-services/unprojection.service';
import { PointCloudContentConnector } from '../point-cloud-content-connector';
import { DollyService } from '../render/cameras/dolly.service';
import { MainCameraService } from '../render/cameras/main-camera.service';
import { OrthographicCameraService } from '../render/cameras/orthographic-camera.service';
import { ProjectionCamerasService } from '../render/cameras/projection-cameras.service';
import { SideViewCamerasService } from '../render/cameras/side-view-cameras.service';
import { RedrawService } from '../render/controls/redraw.service';
import { ShortcutResizeService } from '../render/controls/shortcut-resize.service';
import { ShortcutRotationService } from '../render/controls/shortcut-rotation.service';
import { ShortcutTranslationService } from '../render/controls/shortcut-translation.service';
import { GlobalRenderTriggerService } from '../render/global-render-trigger.service';
import { MaterialService } from '../render/material.service';
import { AxesService } from '../render/mesh/axes/axes.service';
import { PointCloudService } from '../render/mesh/cloud/point-cloud.service';
import { CuboidRouteService } from '../render/mesh/cuboid-route/cuboid-route.service';
import { CuboidService } from '../render/mesh/cuboid/cuboid.service';
import { TrainingCuboidService } from '../render/mesh/cuboid/training-cuboid.service';
import { FreePointMeasurementService } from '../render/mesh/free-point-measurement/free-point-measurement.service';
import { InProgressCuboidService } from '../render/mesh/in-progess-cuboid/in-progress-cuboid.service';
import { MissingShapeMarkerService } from '../render/mesh/missing-shape-marker/missing-shape-marker-service';
import { LineService } from '../render/mesh/poly-shape/line/line.service';
import { TrainingLineService } from '../render/mesh/poly-shape/line/training-line.service';
import { PolygonService } from '../render/mesh/poly-shape/polygon/polygon.service';
import { TrainingPolygonService } from '../render/mesh/poly-shape/polygon/training-polygon.service';
import { SensorIndicatorService } from '../render/mesh/sensor-indicator/sensor-indicator.service';
import { SubpointMeasurementService } from '../render/mesh/subpoint-measurement/subpoint-measurement.service';
import { SceneControllerService } from '../render/scene-controller.service';
import { MainPointCloudRendererCursorService } from '../renderers/main-point-cloud/main-point-cloud-renderer-cursor.service';
import { ViewRegionsService } from '../renderers/view-regions.service';
import { PointCloudContentState } from '../state/point-cloud-content-state';
import { PointCloudContentStateUndoService } from '../state/point-cloud-content-state-undo.service';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import { LoadTaskDataService } from '../task-data/load-task-data.service';
import { InspectService } from '../toolbars/inspect.service';
import { LayerSettingsService3D } from '../toolbars/layer-settings/layer-settings-3d.service';
import { CuboidTool3D } from '../tools/cuboid-tool/cuboid-tool-3d';
import { EditTool3D } from '../tools/edit-tool/edit-tool-3d';
import { MeasureTool } from '../tools/measure-tool/measure-tool';
import { SelectTool3D } from '../tools/select-tool/select-tool-3d';
import { ToolManager3DService } from '../tools/tool-manager-3d.service';
import { KdTreeService } from '../utils/kd-tree.service';
import { PointCloudAssetEventService } from '../utils/loading/point-cloud-asset-event.service';
import { PointCloudAssetService } from '../utils/loading/point-cloud-asset.service';
import { MouseCoordinateService } from '../utils/mouse-coordinate.service';
import { RayCastService } from '../utils/ray-cast.service';
import { CuboidRescaleService } from '../utils/rescale/cuboid-rescale.service';

@Component({
  selector: 'h3-point-cloud-content-initializer',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    contentProviders,
    AxesService,
    CalibrationService,
    CuboidRescaleService,
    CuboidRouteService,
    CuboidService,
    DollyService,
    FreePointMeasurementService,
    GlobalRenderTriggerService,
    InProgressCuboidService,
    InspectService,
    KdTreeService,
    LayerSettingsService3D,
    LineService,
    LoadTaskDataService,
    MainCameraService,
    MainPointCloudRendererCursorService,
    MaterialService,
    MissingShapeMarkerService,
    MouseEventService,
    MouseCoordinateService,
    OrthographicCameraService,
    PointCloudAssetEventService,
    PointCloudAssetService,
    PointCloudContentConnector,
    PointCloudContentStateUndoService,
    PointCloudService,
    PolygonService,
    ProjectionCamerasService,
    ProjectionService,
    RayCastService,
    RedrawService,
    SceneControllerService,
    SensorIndicatorService,
    ShortcutResizeService,
    ShortcutRotationService,
    ShortcutTranslationService,
    SideViewCamerasService,
    SideViewRotationService,
    SubpointMeasurementService,
    ToolManager3DService,
    TrainingCuboidService,
    TrainingLineService,
    TrainingPolygonService,
    UnprojectionService,
    ViewRegionsService,
    makeToolProviders(CuboidTool3D, EditTool3D, MeasureTool, SelectTool3D),
    {
      provide: PointCloudContentStore,
      useFactory: () =>
        new PointCloudContentStore(new PointCloudContentState()),
    },
    {
      provide: AbstractSensorContentStore,
      useExisting: PointCloudContentStore,
    },
  ],
  standalone: false,
})
export class PointCloudContentInitializerComponent
  extends AbstractContentInitializer<PointCloudContentConnector>
  implements OnInit
{
  protected override store!: PointCloudContentStore;

  constructor(
    injector: Injector,
    private layerSettingsService: LayerSettingsService3D,
  ) {
    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();

    this.store('render')('frameNumber').set(this.frameNumber);

    if (!this.store.state().project.use_annotation_store) {
      this.store('render')('layerSettings').set(
        this.layerSettingsService.getInitialSettings(),
      );
    }

    this.subscribeTo(this.store.focusedShapeVisible$, (isAllowed) => {
      this.shellConnector.notifySideViewRendererIsAllowed(isAllowed);
    });

    this.initializeServices();
  }

  protected override setUpSubscriptions(store: PointCloudContentStore) {
    this.setUpSettingsSubscription();

    if (!this.store.state().project.use_annotation_store) {
      this.setUpSubscriptionsObjectless(store);
      return;
    }

    this.subscribeTo(
      store('render')('cuboids').$.pipe(
        map(() => this.buildLayerState()),
        skip(1),
      ),
      (layerState) => {
        this.shellConnector.setShapes(layerState);
      },
    );
  }

  protected getContentConnector() {
    return this.injector.get(PointCloudContentConnector);
  }

  private setUpSubscriptionsObjectless(store: PointCloudContentStore) {
    this.subscribeTo(
      observeShapeGroupOrderChanges(
        merge(
          store('render')('cuboids').$,
          store('render')('lines').$,
          store('render')('polygons').$,
          store('groups').$,
          store('shapeAndGroupOrder').$,
        ).pipe(
          map(() => this.buildLayerStateObjectless()),
          skip(1),
        ),
      ).pipe(
        // We need to check the shapes at depth 1 because the object is
        // recreated on every change, so object equality will always be false
        distinctUntilChanged((previous, current) =>
          isEqualAtDepth(1, previous.shapes, current.shapes),
        ),
      ),
      (layerState) => {
        this.shellConnector.setShapes(layerState);
      },
    );
  }

  private initializeServices() {
    this.injector.get(ViewRegionsService);
    if (this.shellConnector.hasFeatureFlag('camera-calibration-debug_on')) {
      this.injector.get(AxesService);
    }

    if (!this.store.state().readOnly) {
      this.injector.get(ShortcutResizeService);
      this.injector.get(ShortcutRotationService);
      this.injector.get(ShortcutTranslationService);
    }

    // un-migrated services not using prepareToRender approach
    this.injector.get(CuboidRouteService);
  }

  private buildLayerStateObjectless(): ShapeGroupOrderState {
    const state = this.store.state();
    const orthographicShapes = {
      ...get(state, ['render', 'lines']),
      ...get(state, ['render', 'polygons']),
    };
    return {
      shapes: {
        ...get(state, ['render', 'cuboids']),
        ...orthographicShapes,
      },
      shapeAndGroupOrder: [
        ...get(state, ['render', 'shapeAndGroupOrder'], []),
        ...keys(orthographicShapes).map((index) => ({
          type: 'shape',
          index,
        })),
      ],
      groups: {
        ...get(state, ['render', 'groups']),
      },
    };
  }

  private buildLayerState(): ShapeGroupOrderState {
    const state = this.store.state();
    return {
      shapes: get(state, ['render', 'cuboids']),
      shapeAndGroupOrder: [],
      groups: {},
    };
  }

  private setUpSettingsSubscription() {
    if (this.store.state().project.use_annotation_store) {
      this.subscribeTo(this.settingsStore('layerSettings3d').$, (settings) =>
        this.store('render')('layerSettings').set(settings),
      );

      this.subscribeTo(
        this.settingsStore('regionOfInterestSettings').$,
        (settings) => this.store('regionOfInterestSettings').set(settings),
      );

      this.subscribeTo(
        this.settingsStore('viewBoxHighlightEnabled').$,
        (settings) => this.store('viewBoxHighlightEnabled').set(settings),
      );
    }

    this.setUpSettingsOutsideOfPanelSubscription();
  }

  private setUpSettingsOutsideOfPanelSubscription() {
    this.subscribeTo(this.settingsStore.$, (settings) => {
      this.store('render')('hideGround').set(settings.hideGround);
      this.store('render')('viewMode').set(settings.viewMode);
      this.store('render')('showRoute').set(settings.showRoute);
      this.store('render')('calibrationOptions').set(
        settings.calibrationOptions,
      );
      this.store('render')('enhancedCloudSettings').set(
        settings.enhancedCloudSettings,
      );
      this.store('render')('projectionSettings')('showShapes').set(
        settings.showShapeProjection,
      );
      this.store('render')('projectionSettings')('showClouds').set(
        settings.showCloudProjection,
      );
    });
  }
}
